import React, { useState } from "react";
import { Card, CardHeader, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Calculator, TrendingDown, Euro, Zap } from "lucide-react";

const CalculatorSection = () => {
  const [formData, setFormData] = useState({
    activityType: "",
    consumption: "",
    monthlyBill: ""
  });
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const activityTypes = [
    { value: "restaurant", label: "Restaurant", savingRate: 0.22 },
    { value: "commerce", label: "Commerce de détail", savingRate: 0.18 },
    { value: "bureau", label: "Bureaux", savingRate: 0.20 },
    { value: "industrie", label: "Industrie", savingRate: 0.25 },
    { value: "hotel", label: "Hôtellerie", savingRate: 0.19 },
    { value: "medical", label: "Cabinet médical", savingRate: 0.16 },
    { value: "copropriete", label: "Copropriété", savingRate: 0.28 },
    { value: "particulier", label: "Particulier", savingRate: 0.15 },
    { value: "collectivite", label: "Collectivité", savingRate: 0.30 },
    { value: "other", label: "Autre", savingRate: 0.20 }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const calculateSavings = () => {
    if (!formData.activityType || !formData.monthlyBill) {
      return;
    }

    setIsCalculating(true);
    
    // Simulate calculation delay for better UX
    setTimeout(() => {
      const selectedActivity = activityTypes.find(type => type.value === formData.activityType);
      const monthlyAmount = parseFloat(formData.monthlyBill);
      const savingRate = selectedActivity?.savingRate || 0.20;
      
      const monthlySavings = monthlyAmount * savingRate;
      const annualSavings = monthlySavings * 12;
      const percentageSavings = Math.round(savingRate * 100);

      setResult({
        monthlySavings: Math.round(monthlySavings),
        annualSavings: Math.round(annualSavings),
        percentageSavings,
        activityLabel: selectedActivity?.label || "Votre activité"
      });
      
      setIsCalculating(false);
    }, 1500);
  };

  const resetCalculator = () => {
    setFormData({
      activityType: "",
      consumption: "",
      monthlyBill: ""
    });
    setResult(null);
  };

  return (
    <section id="calculator" className="py-20 bg-gradient-to-b from-white to-green-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center p-3 bg-green-100 rounded-full mb-6">
            <Calculator className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Calculez vos économies potentielles
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Découvrez combien vous pourriez économiser sur votre facture énergétique avec nos services de courtage
          </p>
        </div>

        <Card className="bg-white shadow-xl border-0 rounded-2xl overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-8">
            <h3 className="text-2xl font-bold text-center">
              Simulation d'économies énergétiques
            </h3>
            <p className="text-green-100 text-center mt-2">
              Estimation basée sur nos résultats clients moyens
            </p>
          </CardHeader>
          
          <CardContent className="p-8">
            {!result ? (
              <div className="space-y-6">
                {/* Activity Type */}
                <div className="space-y-3">
                  <Label htmlFor="activity" className="text-lg font-semibold text-gray-700">
                    Type d'activité *
                  </Label>
                  <Select value={formData.activityType} onValueChange={(value) => handleInputChange("activityType", value)}>
                    <SelectTrigger className="h-12 text-lg">
                      <SelectValue placeholder="Sélectionnez votre type d'activité" />
                    </SelectTrigger>
                    <SelectContent>
                      {activityTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Consumption */}
                <div className="space-y-3">
                  <Label htmlFor="consumption" className="text-lg font-semibold text-gray-700">
                    Consommation annuelle (kWh) - Optionnel
                  </Label>
                  <Input
                    id="consumption"
                    type="number"
                    placeholder="Ex: 50000"
                    value={formData.consumption}
                    onChange={(e) => handleInputChange("consumption", e.target.value)}
                    className="h-12 text-lg"
                  />
                  <p className="text-sm text-gray-500">
                    Information disponible sur votre facture d'électricité
                  </p>
                </div>

                {/* Monthly Bill */}
                <div className="space-y-3">
                  <Label htmlFor="monthlyBill" className="text-lg font-semibold text-gray-700">
                    Facture mensuelle moyenne (€) *
                  </Label>
                  <Input
                    id="monthlyBill"
                    type="number"
                    placeholder="Ex: 1200"
                    value={formData.monthlyBill}
                    onChange={(e) => handleInputChange("monthlyBill", e.target.value)}
                    className="h-12 text-lg"
                  />
                  <p className="text-sm text-gray-500">
                    Montant total électricité + gaz si applicable
                  </p>
                </div>

                <Button
                  onClick={calculateSavings}
                  disabled={!formData.activityType || !formData.monthlyBill || isCalculating}
                  className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:opacity-50"
                >
                  {isCalculating ? (
                    <div className="flex items-center space-x-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      <span>Calcul en cours...</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <Calculator className="h-5 w-5" />
                      <span>Calculer mes économies</span>
                    </div>
                  )}
                </Button>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Results */}
                <div className="text-center mb-8">
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">
                    Vos économies potentielles
                  </h4>
                  <p className="text-gray-600">
                    Basé sur votre profil : {result.activityLabel}
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                  <Card className="p-6 text-center bg-emerald-50 border-emerald-200">
                    <div className="flex items-center justify-center mb-3">
                      <Euro className="h-8 w-8 text-emerald-600" />
                    </div>
                    <div className="text-3xl font-bold text-emerald-600 mb-2">
                      {result.monthlySavings}€
                    </div>
                    <div className="text-gray-600 font-medium">
                      Économies mensuelles
                    </div>
                  </Card>

                  <Card className="p-6 text-center bg-green-50 border-green-200">
                    <div className="flex items-center justify-center mb-3">
                      <TrendingDown className="h-8 w-8 text-green-600" />
                    </div>
                    <div className="text-3xl font-bold text-green-600 mb-2">
                      {result.annualSavings}€
                    </div>
                    <div className="text-gray-600 font-medium">
                      Économies annuelles
                    </div>
                  </Card>

                  <Card className="p-6 text-center bg-emerald-50 border-emerald-200">
                    <div className="flex items-center justify-center mb-3">
                      <Zap className="h-8 w-8 text-emerald-600" />
                    </div>
                    <div className="text-3xl font-bold text-emerald-600 mb-2">
                      {result.percentageSavings}%
                    </div>
                    <div className="text-gray-600 font-medium">
                      de réduction
                    </div>
                  </Card>
                </div>

                <div className="bg-gradient-to-r from-emerald-50 to-green-50 p-6 rounded-xl border-l-4 border-emerald-500">
                  <h5 className="font-bold text-gray-900 mb-2">
                    💡 Ces économies sont-elles réalistes ?
                  </h5>
                  <p className="text-gray-700">
                    Ces estimations sont basées sur les résultats moyens obtenus par nos clients dans votre secteur d'activité. 
                    Les économies réelles dépendent de votre situation spécifique et peuvent être supérieures.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    onClick={() => document.querySelector("#contact").scrollIntoView({ behavior: "smooth" })}
                    className="flex-1 h-12 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold"
                  >
                    Demander un audit gratuit
                  </Button>
                  <Button
                    onClick={resetCalculator}
                    variant="outline"
                    className="flex-1 h-12 border-2 border-gray-300 hover:bg-gray-50"
                  >
                    Nouvelle simulation
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Additional Info */}
        <div className="mt-12 text-center">
          <p className="text-gray-600">
            <strong>Audit personnalisé gratuit :</strong> Nos experts analysent votre situation réelle 
            pour vous proposer des économies optimisées et sur-mesure.
          </p>
        </div>
      </div>
    </section>
  );
};

export default CalculatorSection;